import * as React from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  RetryProvisionFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type RetryProvisionFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderSelectControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { coerceFieldValue } from '@/lib/utils'

export { RetryProvisionFormSchema, type RetryProvisionFormData } from './schema'

export type RetryProvisionComposerProps = {
  deviceNumber: number
  deviceProvisioningInfoNumber?: number
  previousNetworkCorridorNumber?: number
  previousNetworkSiteNumber?: number
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof RetryProvisionFormData, value: unknown): string | null => {
  const shape = RetryProvisionFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function RetryProvisionComposer(props: RetryProvisionComposerProps) {
  const {
    deviceNumber,
    deviceProvisioningInfoNumber,
    previousNetworkCorridorNumber,
    previousNetworkSiteNumber,
    onSuccess,
    onError,
    onCancel,
    showHeader = true,
    submitLabel,
  } = props

  // Track selected corridor for cascading site options
  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const [selectedCorridor, setSelectedCorridor] = React.useState<string>(
    previousNetworkCorridorNumber ? String(previousNetworkCorridorNumber) : '',
  )
  // Fetch options (same as ProvisionDeviceComposer)
  const { optionsMap: corridorOptionsMap, loadingMap: corridorLoadingMap } = useSelectOptionsMap({
    networkCorridor: {
      endpoint: 'getNetworkCorridors',
      dataKey: 'networkCorridors',
      valueField: 'networkCorridorNumber',
      labelField: 'networkCorridorName',
    },
  })

  const { optionsMap: siteOptionsMap, loadingMap: siteLoadingMap } = useSelectOptionsMap({
    networkSite: {
      endpoint: 'getNetworkSites',
      dataKey: 'networkSites',
      valueField: 'networkSiteNumber',
      labelField: 'networkSiteName',
      queryParams: selectedCorridor ? { networkCorridorNumber: parseInt(selectedCorridor) } : {},
      queryOptions: {
        enabled: !!selectedCorridor,
      },
    },
  })

  // Fetch the device so we can preserve its fields and re-link the newly created
  // provisioning record to it after reprovisioning.
  const { data: deviceDataRaw } = useApiQuery('getDeviceByNumber', { deviceNumber })
  const deviceData = deviceDataRaw?.content

  // Reprovision = create a NEW provisioning record (status 'requested').
  const retryProvisionMutation = useApiMutation('upsertDeviceProvisioningInfo', 'POST', {
    invalidates: ['getDevices', 'getDeviceByNumber'],
    onError: (error) => {
      onError?.(error)
    },
  })

  // Link the new provisioning record's number back onto the device.
  const associateDevice = useApiMutation('upsertDevice', 'POST', {
    invalidates: ['getDevices', 'getDeviceByNumber'],
    onError: (error) => {
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: RetryProvisionFormSchema,
    defaultValues: {
      networkCorridor: previousNetworkCorridorNumber ? String(previousNetworkCorridorNumber) : '',
      networkSite: previousNetworkSiteNumber ? String(previousNetworkSiteNumber) : '',
    } as RetryProvisionFormData,
    onSubmit: async ({ value }) => {
      const formValue = value as RetryProvisionFormData
      const { provisioningInfo } = transformToApiRequest(formValue, deviceNumber)

      // 1) Create a fresh provisioning request → deviceProvisioningStatus becomes 'requested'.
      const response = await retryProvisionMutation.mutateAsync({ deviceNumber, provisioningInfo })

      // 2) Read the new provisioning record's number and associate it with the device.
      const newProvisioningInfoNumber =
        (response as any)?.content?.provisioningInfo?.deviceProvisioningInfoNumber ?? deviceProvisioningInfoNumber

      await associateDevice.mutateAsync({
        device: {
          ...deviceData?.device,
          deviceNumber,
          deviceProvisioningInfoNumber: newProvisioningInfoNumber,
        },
      })

      onSuccess?.()
    },
  })

  // Update selectedCorridor when it changes
  React.useEffect(() => {
    if (selectedCorridor) {
      form.setFieldValue('networkSite', '')
    }
  }, [selectedCorridor, form])

  const renderControl = (control: any) => {
    const isRequired = control.required

    if (control.htmlControl === 'select') {
      let options: any[] = []
      let isLoading = false

      if (control.name === 'networkCorridor') {
        options = corridorOptionsMap.networkCorridor || []
        isLoading = corridorLoadingMap.networkCorridor || false
      } else if (control.name === 'networkSite') {
        options = siteOptionsMap.networkSite || []
        isLoading = siteLoadingMap.networkSite || false
      } else {
        options = FormConfigLoader.normalizeSelectOptions(control.selectOptions || [])
        isLoading = false
      }

      return (
        <form.AppField
          key={control.name}
          name={control.name}
          validators={{
            onChange: ({ value }) => {
              if (control.name === 'networkCorridor') {
                setSelectedCorridor(String(value || ''))
              }
              return undefined
            },
            onBlur: ({ value }) =>
              validateField(control.name as keyof RetryProvisionFormData, coerceFieldValue(control.name, value)) || undefined,
          }}
        >
          {(bound) => {
            const config: ControlConfig = {
              name: control.name,
              label: control.label,
              placeholder: control.placeholder,
              required: isRequired,
              visibility: control.visibility ?? true,
              disabled: (control.disabled ?? false) || (control.name === 'networkSite' && !selectedCorridor),
              options: options,
              isLoading: isLoading,
            }
            return renderSelectControl(config, bound)
          }}
        </form.AppField>
      )
    }

    return null
  }

  const isSubmitting = retryProvisionMutation.isPending || associateDevice.isPending

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {FORM_CONFIG.formDescription}
            </p>
          </div>
        )}

        <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm">
          <p className="text-destructive font-medium">Previous provisioning attempt failed.</p>
          <p className="text-muted-foreground">Update the network location and try again.</p>
        </div>

        {groups.map((group) => {
          const groupControls = controlsByGroup.get(group.formGroupName) || []
          if (groupControls.length === 0) return null
          return (
            <div key={group.formGroupName} className="space-y-4">
              {groupControls.map(renderControl)}
            </div>
          )
        })}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}

          {renderButtonControl({
            label: isSubmitting 
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Provisioning...') 
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Retry Provision'),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}
